/**
 * Draft-mode hand-offs open a ticket without switching the AI off, so the AI
 * keeps writing drafts for the team while the ticket is open. Those tickets get
 * different copy and a single Resolve: offering to "keep the AI off" would put a
 * hold on the conversation and stop the drafts.
 */

export interface ConversationAiState {
  aiPaused?: boolean | null;
  handoffStatus?: string | null;
}

/** The business's two reply modes, as replyModesFrom (reply-modes.ts) returns them. */
export interface ReplyModePairLike {
  openHoursMode?: string | null;
  closedHoursMode?: string | null;
}

const OPEN_HANDOFF = ['pending', 'active'];

/**
 * True when the business drafts replies for its team in at least one period
 * (open or closed hours). A business on Auto only has no Draft tickets, so it
 * keeps the usual resume and keep-off choices.
 */
export function businessDraftsReplies(modes?: ReplyModePairLike | null): boolean {
  return modes?.openHoursMode === 'assist' || modes?.closedHoursMode === 'assist';
}

/**
 * True when the business drafts for its team and the conversation says its
 * hand-off is open while the AI is not paused. A Draft ticket never writes
 * aiPaused, so a missing value is the schema default (false); every other way
 * a hand-off opens sets aiPaused true in the same write. A screen that has not
 * caught up (no open hand-off status yet) keeps the usual copy.
 */
export function handoffKeepsDrafting(
  conversation: ConversationAiState | null | undefined,
  businessDrafts: boolean,
): boolean {
  if (!businessDrafts || !conversation) return false;
  return conversation.aiPaused !== true && OPEN_HANDOFF.includes(conversation.handoffStatus ?? '');
}

/**
 * For a pending ticket in the escalation queue: the conversation the server
 * sent with the ticket wins; otherwise the copy the inbox already has loaded.
 * A pending ticket means an open hand-off unless the conversation says otherwise.
 */
export function ticketKeepsDrafting(
  ticket: { status?: string; conversation?: ConversationAiState | null },
  loadedConversation: ConversationAiState | null | undefined,
  businessDrafts: boolean,
): boolean {
  if (ticket.status !== 'pending' && ticket.status !== 'active') return false;
  if (ticket.conversation) {
    return handoffKeepsDrafting(
      { ...ticket.conversation, handoffStatus: ticket.conversation.handoffStatus ?? 'pending' },
      businessDrafts,
    );
  }
  return handoffKeepsDrafting(loadedConversation, businessDrafts);
}
